import React, { useContext } from 'react';
import { CleanRoomsLinkContext } from 'pages/CleanRooms/CleanRoomsLinkContextProvider';
import { Controller, useForm } from 'react-hook-form';
import { HDialog, HDialogContent } from 'BaseComponents';
import {
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { cleanRoom } from 'utils/spaUrls';
import { useCreateLiveCleanroom, useRecreateLiveCleanroom } from 'api/liveCleanroom';
import { useHistory } from 'react-router-dom';


const HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => i + 1);

const CreateLiveCleanroomDialog = ({
  open, onClose, cleanroom, isRecreate = false, existingLcrId,
}) => {
  const {
    ID: crId,
  } = cleanroom;

  const history = useHistory();
  const { dataContextRef } = useContext(CleanRoomsLinkContext);

  const {
    handleSubmit, control, reset,
  } = useForm({
    defaultValues: {
      hours: 1,
    },
  });

  const {
    isLoading: isCreatingLiveCleanroom,
    mutate: createLiveCleanroom,
    error: errorCreateLiveCleanroom,
    reset: resetCreateLiveCleanroom,
  } = useCreateLiveCleanroom(crId);

  const {
    isLoading: isRecreatingLiveCleanroom,
    mutate: recreateLiveCleanroom,
    error: errorRecreateLiveCleanroom,
    reset: resetRecreateLiveCleanroom,
  } = useRecreateLiveCleanroom(crId, existingLcrId);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data) => {
    const liveCrSessionDuration = data.hours;
    const payload = { sessionDuration: liveCrSessionDuration };
    const successCallback = {
      onSuccess: () => {
        if (dataContextRef?.current?.refreshListData) {
          dataContextRef.current.refreshListData();
        }
        handleClose();
      },
    };

    if (isRecreate) {
      recreateLiveCleanroom(payload, successCallback);
    }
    else {
      createLiveCleanroom(payload, successCallback);
    }
  };

  return (
    <>
      <HDialog
        open={open}
        fullWidth
        maxWidth='sm'
        title={isRecreate ? 'Recreate Live Cleanroom' : 'Live Cleanroom'}
        onClose={handleClose}
        onSubmit={handleSubmit(onSubmit)}
        loading={isCreatingLiveCleanroom || isRecreatingLiveCleanroom}
        submitText={isRecreate ? 'Recreate' : 'Create'}
      >
        <HDialogContent>
          <Typography variant='body2' mb={2}>
            Set a time period between 1 hour and 48 hours for your live cleanroom session.
          </Typography>

          <Stack direction='row' spacing={2} alignItems='center'>
            <Controller
              name='hours'
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label='Hours'
                  {...field}
                  variant='outlined'
                  size='small'
                  fullWidth
                >
                  {HOUR_OPTIONS.map((hour) => (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </HDialogContent>
      </HDialog>
      {((errorCreateLiveCleanroom && !isRecreate) || (errorRecreateLiveCleanroom && isRecreate))
        && ((errorCreateLiveCleanroom?.response?.data?.message === 'No datasets configured for Live Clean Room')
            || (errorRecreateLiveCleanroom?.response?.data?.message === 'No datasets configured for Live Clean Room')) && (
            <HDialog
              open
              title={`Error ${isRecreate ? 'Recreating' : 'Creating'} Live Cleanroom`}
              onClose={isRecreate ? resetRecreateLiveCleanroom : resetCreateLiveCleanroom}
              submitText='Configure'
              onSubmit={() => history.push(cleanRoom.manageDatasets(crId))}
              maxWidth='sm'
            >
              <HDialogContent>
                <Typography color='error' variant='body1' marginBottom={1} fontWeight='500'>
                  {(isRecreate ? errorRecreateLiveCleanroom : errorCreateLiveCleanroom)?.response?.data?.message || 'An unexpected error occurred.'}
                </Typography>
                Please click Configure to set up datasets for your Live Clean Room.
              </HDialogContent>
            </HDialog>
      )}
    </>
  );
};

export default CreateLiveCleanroomDialog;
